import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/api';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  permissions: {
    esAdministradorGrupo?: boolean;
    clientesAdministrados?: number[];
    nombreGrupo?: string | null;
    [key: string]: any;
  };
  clienteId?: number;
  lastLogin?: string;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (userData: User, token: string) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  checkForceLogout: () => boolean;
  forceCleanLogout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkForceLogout = () => {
    if (user) {
      const forceLogoutKey = `force_logout_${user.id}`;
      const forceLogoutTime = localStorage.getItem(forceLogoutKey);
      
      if (forceLogoutTime) {
        localStorage.removeItem('token');
        localStorage.removeItem(forceLogoutKey);
        alert('Tu sesión fue cerrada por un administrador');
        setUser(null);
        return true;
      }
    }
    return false;
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verificar si fue desconectado forzosamente antes de hacer API call
      if (checkForceLogout()) {
        setLoading(false);
        return;
      }

      const response = await authApi.me();
      if (response.data.success) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = (userData: User, token: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('currentUser', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = async () => {
    try {
      // Llamar al endpoint del backend para cerrar sesión de trabajo
      await authApi.logout();
    } catch (error) {
      console.error('Error cerrando sesión en el backend:', error);
      // Continuar con el logout local incluso si hay error
    } finally {
      // Limpiar COMPLETAMENTE el localStorage y estado local
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');

      // Limpiar otros posibles datos relacionados con la sesión
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('force_logout_') || key.includes('user_') || key.includes('session_')) {
          localStorage.removeItem(key);
        }
      });

      setUser(null);

      // Forzar recarga de la página para asegurar limpieza completa
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const forceCleanLogout = () => {
    // Limpiar TODO el localStorage
    localStorage.clear();

    // Reiniciar estado
    setUser(null);
    setLoading(false);

    // Forzar recarga completa de la página
    window.location.href = '/login';
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    checkForceLogout,
    forceCleanLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};